import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";

import StepWizard from "react-step-wizard";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { gql } from "@apollo/client";
import shopifyLogo from "@assets/shopify-mini-logo.png";
import { GenericTooltip } from "@components/tooltip-container";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { ConnectionType } from "@north-beam/nb-client-side";
import { type PixelPlacementInfoQuery } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { useSideNavContext } from "@pages/onboarding/onboarding-nav/onboarding-nav-item";
import { useOnboardingStep } from "@pages/onboarding/onboarding-nav/use-onboarding-number";
import { NorthbeamPixelTag } from "@pages/settings/tracking-script-tags/northbeam-pixel-tag";
import { OnboardingTrackingScript } from "@pages/settings/tracking-script-tags/onboarding-tracking-script";
import { ShopifyOrderStatusTag } from "@pages/settings/tracking-script-tags/shopify-order-status-tag";
import { ShopifyPostPurchaseUpsellTag } from "@pages/settings/tracking-script-tags/shopify-post-purchase-upsell-tag";

import { type SubNavItem } from "../..";
import { Error } from "../../error";
import {
  isVisible,
  scrollToSubStepElement,
} from "../../onboarding-nav/scroll-utils";
import { Steps } from "../../steps";
import { WarningPanel } from "../components/warning-panel";
import { Step, type StepChildProps } from "../step";
import { StepCompletionConfirmation } from "../step-completion-confirmation";
import { StepContainer } from "../step-container";

import {
  VERIFY_SETUP_TITLE,
  VerifySetup,
  type PixelVerificationStatus,
} from "./verify-setup";

const STEP_BY_STEP_LINK =
  "https://docs.northbeam.io/docs/5-install-the-northbeam-pixel";

export function NeedHelpWithPixel({
  text = "Need help setting up the pixel?",
  link = STEP_BY_STEP_LINK,
}: {
  link?: string;
  text?: string;
}) {
  return (
    <div
      className="w-[792px] h-[117px] p-6 bg-cosmic-5
rounded-lg border border-neutral-300 flex-col justify-end items-start gap-4 inline-flex"
    >
      <div className="self-stretch justify-start items-center gap-2 inline-flex">
        <div className="relative">
          <div className="w-[18px] h-[18px] bg-cosmic rounded-full border border-black flex justify-center items-center">
            <i
              className="fa-solid fa-question fa-xs"
              style={{ color: "#ffffff" }}
            ></i>
          </div>
        </div>
        <div className="grow shrink basis-0 text-slate-900 text-lg font-semibold leading-normal">
          {text}
        </div>
      </div>
      <div className="justify-start items-end gap-2 inline-flex">
        <a
          target="_blank"
          href={link}
          className="no-underline justify-start items-end gap-4 flex"
          rel="noreferrer"
        >
          <div className="px-4 py-1.5 bg-white rounded-2xl border border-neutral-300 justify-center items-center gap-2 flex">
            <i
              className="fa-regular fa-file fa-xs"
              style={{ color: "#7081FF" }}
            ></i>
            <div className="text-interstellar+2 text-xs font-semibold uppercase leading-tight tracking-tight">
              Read the documentation
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}

export const PixelPlacementSteps: FC<StepChildProps> = ({
  nextStep,
  previousStep,
  isMarkedComplete,
  toggleStepComplete,
  ...props
}) => {
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["pixel-placement"].index;
  const { SUBNAV_ITEMS, SUBNAV_ITEMS_WITH_CHECK } = useMemo(() => {
    const _SUBNAV_ITEMS = {
      other: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Install the pixel on every page of your site`,
          subStep: "b",
        },
        {
          label: `${stepIndex}c.  Implement the 'firePurchaseEvent' JavaScript function at conversion points`,
          subStep: "c",
        },
      ],
      shopify: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please install the pixel on every page of your site`,
          subStep: "b",
        },
      ],
      headless: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please install the pixel on every page of your site`,
          subStep: "b",
        },
      ],
    };

    const _SUBNAV_ITEMS_WITH_CHECK = {
      other: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Install the pixel on every page of your site`,
          subStep: "b",
        },
        {
          label: `${stepIndex}c.  Implement the 'firePurchaseEvent' JavaScript function at conversion points`,
          subStep: "c",
        },
        {
          label: `${stepIndex}d. ` + VERIFY_SETUP_TITLE,
          subStep: "d",
        },
      ],
      shopify: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please install the pixel on every page of your site`,
          subStep: "b",
        },
        {
          label: `${stepIndex}c. ` + VERIFY_SETUP_TITLE,
          subStep: "c",
        },
      ],
      headless: [
        {
          label: `${stepIndex}a.  Please select your e-commece platform`,
          subStep: "a",
        },
        {
          label: `${stepIndex}b.  Please install the pixel on every page of your site`,
          subStep: "b",
        },
        {
          label: `${stepIndex}c. ` + VERIFY_SETUP_TITLE,
          subStep: "c",
        },
      ],
    };
    return {
      SUBNAV_ITEMS: _SUBNAV_ITEMS,
      SUBNAV_ITEMS_WITH_CHECK: _SUBNAV_ITEMS_WITH_CHECK,
    };
  }, [stepIndex]);

  const { hasFeatureFlag } = useFeatureFlags();
  const { setSubNavItems } = useSideNavContext();
  const { data, loading, error } = useNorthbeamQuery<PixelPlacementInfoQuery>(
    USER_QUERY,
    {
      variables: {
        connectionType: ConnectionType.integration_shopify,
      },
    },
  );

  const [stepMetadata, setStepMetadata] = useState(props.stepMetadata);
  const updateStepMetadata = (stepMetadata: any) => {
    setStepMetadata(stepMetadata);
    props.updateStepMetadata(stepMetadata);
  };

  const domainName = data?.me.domainName;

  useEffect(() => {
    if (!stepMetadata || loading || !domainName) {
      return;
    }
    const subNavItems = SUBNAV_ITEMS_WITH_CHECK;
    setSubNavItems(
      subNavItems[
        stepMetadata.provider as keyof typeof subNavItems
      ] as SubNavItem[],
    );
  }, [
    setSubNavItems,
    stepMetadata,
    domainName,
    loading,
    hasFeatureFlag,
    SUBNAV_ITEMS_WITH_CHECK,
    SUBNAV_ITEMS,
  ]);

  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });

  useEffect(() => {
    if (searchParams.noScroll) {
      setSearchParams({ noScroll: false });
    }
  }, []);

  const aScrollRef = useRef<(() => void) | null>(null);
  const bScrollRef = useRef<(() => void) | null>(null);
  const cScrollRef = useRef<(() => void) | null>(null);
  const dScrollRef = useRef<(() => void) | null>(null);

  const aTitleRef = useCallback(
    (node: any) => {
      if (node !== null) {
        aScrollRef.current = node;
        scrollToSubStepElement(node, searchParams, "a");
      }
    },
    [searchParams],
  );
  const bTitleRef = useCallback(
    (node: any) => {
      bScrollRef.current = node;
      if (node !== null) {
        scrollToSubStepElement(node, searchParams, "b");
      }
    },
    [searchParams],
  );

  const cTitleRef = useCallback(
    (node: any) => {
      cScrollRef.current = node;
      if (node !== null) {
        scrollToSubStepElement(node, searchParams, "c");
      }
    },
    [searchParams],
  );

  const dTitleRef = useCallback(
    (node: any) => {
      dScrollRef.current = node;
      if (node !== null) {
        scrollToSubStepElement(node, searchParams, "d");
      }
    },
    [searchParams],
  );

  return (
    <StepWizard isLazyMount className="onboarding-step-wizard">
      <StepContainer
        onScrollStopped={() => {
          if (isVisible(aScrollRef, 20)) {
            setSearchParams({
              subStep: "a",
              noScroll: true,
            });
          } else if (isVisible(cScrollRef, 20)) {
            setSearchParams({
              subStep: "c",
              noScroll: true,
            });
          } else if (isVisible(bScrollRef, 20)) {
            setSearchParams({
              subStep: "b",
              noScroll: true,
            });
          } else if (isVisible(dScrollRef, 20)) {
            setSearchParams({
              subStep: "d",
              noScroll: true,
            });
          }
        }}
        isLast={false}
        title={`${stepIndex}. Complete Pixel Placement`}
        onMoveBackwardButtonClicked={() => {
          setSearchParams({
            step: Steps.DNS_SETUP,
          });
          previousStep && previousStep();
        }}
        onMoveForwardButtonClicked={() => {
          setSearchParams({
            step: Steps.ORDERS_API,
          });
          nextStep && nextStep();
        }}
        hideFooter={loading}
        knowledgeBaseLink="https://docs.northbeam.io/docs/add-pixel"
        knowledgeBaseText="Add Pixel"
        stepCompletionConfirmation={
          stepMetadata?.provider ? (
            <GenericTooltip
              showTooltip={!stepMetadata?.isPixelVerified}
              content={`${
                domainName
                  ? "Pixel setup must be verified"
                  : "Step Add Domain must be complete"
              } before the step can be marked as ‘Complete’.`}
              placement="top-start"
              noInfoCircle
            >
              <StepCompletionConfirmation
                labelSuffix="all the steps for adding pixels"
                step={Steps.PIXEL_PLACEMENT}
                nextStep={() => {
                  setSearchParams({
                    step: Steps.ORDERS_API,
                  });
                  nextStep && nextStep();
                }}
                toggleStepComplete={toggleStepComplete}
                disabled={!isPixelVerified(stepMetadata)}
              />
            </GenericTooltip>
          ) : undefined
        }
      >
        <Step
          nextStep={() => {
            if (!isMarkedComplete && stepMetadata?.provider) {
              toggleStepComplete();
            }
            setSearchParams({
              step: Steps.ORDERS_API,
            });
            nextStep && nextStep();
          }}
          nextStepButtonText={`${stepDictionary["orders-api"].index}. Add Orders`}
          loading={loading}
        >
          {!domainName ? (
            <div className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10">
              <div className="text-2xl font-semibold leading-loose pb-6">
                Please complete Add Domain Step to setup your Pixel
              </div>
              You must complete{" "}
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  setSearchParams({ step: Steps.DOMAIN });
                  props.goToNamedStep?.(Steps.DOMAIN);
                }}
              >
                Step Add Domain
              </a>{" "}
              before this step can be completed.
            </div>
          ) : (
            <>
              <div
                ref={aTitleRef}
                className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10"
              >
                <div className="text-slate-900 text-2xl font-semibold leading-loose pb-6">
                  {stepIndex}a. Please select your e-commerce platform
                </div>
                <div className="flex gap-6 flex-col">
                  <GenericTooltip
                    content={
                      "Uncheck the 'Complete' checkbox at the bottom of the screen to update"
                    }
                    showTooltip={isMarkedComplete}
                    noInfoCircle
                    placement="top-start"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        disabled={isMarkedComplete}
                        id="shopify"
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateStepMetadata({
                              provider: "shopify",
                            });
                          }
                        }}
                        checked={stepMetadata?.provider === "shopify"}
                        type="radio"
                      />
                      <label
                        className="flex items-center mb-0 gap-2"
                        htmlFor="shopify"
                      >
                        <img
                          src={shopifyLogo}
                          alt="shopify Logo"
                          className="w-5 h-5"
                        />
                        <div>Shopify Pixel</div>
                      </label>
                    </div>
                  </GenericTooltip>
                  <GenericTooltip
                    content={
                      "Uncheck the 'Complete' checkbox at the bottom of the screen to update"
                    }
                    showTooltip={isMarkedComplete}
                    noInfoCircle
                    placement="top-start"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        id="headless"
                        disabled={isMarkedComplete}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateStepMetadata({
                              provider: "headless",
                            });
                          }
                        }}
                        checked={stepMetadata?.provider === "headless"}
                        type="radio"
                      />
                      <label
                        className="flex items-center mb-0 gap-2"
                        htmlFor="headless"
                      >
                        <img
                          src={shopifyLogo}
                          alt="shopify Logo"
                          className="w-5 h-5"
                        />
                        <div>
                          We use Shopify for checkout, but have a custom site
                          (Headless Solution)
                        </div>
                      </label>
                    </div>
                  </GenericTooltip>
                  <GenericTooltip
                    content={
                      "Uncheck the 'Complete' checkbox at the bottom of the screen to update"
                    }
                    showTooltip={isMarkedComplete}
                    placement="top-start"
                    noInfoCircle
                  >
                    <div className="flex items-start gap-2">
                      <input
                        disabled={isMarkedComplete}
                        id="other"
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateStepMetadata({
                              provider: "other",
                            });
                          }
                        }}
                        checked={stepMetadata?.provider === "other"}
                        type="radio"
                      />
                      <label
                        className="flex flex-col mb-0 gap-2 -mt-2"
                        htmlFor="other"
                      >
                        <div>
                          Other e-commerce platform or custom solution (e.g
                          Salesforce, Data Lakes)
                        </div>
                        <div className="text-sm font-semibold">
                          Only available on Pro and Enterprise Plans
                        </div>
                      </label>
                    </div>
                  </GenericTooltip>
                </div>
              </div>
              {stepMetadata?.provider === "shopify" && (
                <>
                  <div
                    ref={bTitleRef}
                    className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 "
                  >
                    <div className="text-slate-900 text-2xl font-semibold leading-loose">
                      {stepIndex}b. Please install the pixel on every page of
                      your site
                    </div>
                    <div className="w-full">
                      <NorthbeamPixelTag
                        installationScripts={data?.me?.installationScripts}
                      />
                      {stepMetadata?.provider === "shopify" && (
                        <>
                          <ShopifyOrderStatusTag
                            installationScripts={data?.me?.installationScripts}
                          />
                          <ShopifyPostPurchaseUpsellTag
                            installationScripts={data?.me?.installationScripts}
                          />
                        </>
                      )}
                    </div>
                    <NeedHelpWithPixel link="https://docs.northbeam.io/docs/shopify-installation" />
                  </div>
                  <VerifySetupWrapper
                    subStep="c"
                    subStepRef={cTitleRef}
                    stepMetadata={stepMetadata}
                    updateStepMetadata={updateStepMetadata}
                    isMarkedComplete={isMarkedComplete}
                    domainName={domainName ?? undefined}
                  />
                </>
              )}
              {stepMetadata?.provider === "headless" && (
                <>
                  <div
                    ref={bTitleRef}
                    className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10"
                  >
                    <div className="text-slate-900 text-2xl font-semibold leading-loose">
                      {stepIndex}b. Please install the pixel on every page of
                      your site
                    </div>
                    <div className="w-full">
                      <NorthbeamPixelTag
                        installationScripts={data?.me?.installationScripts}
                      />
                      <ShopifyOrderStatusTag
                        installationScripts={data?.me?.installationScripts}
                      />
                      <ShopifyPostPurchaseUpsellTag
                        installationScripts={data?.me?.installationScripts}
                      />
                    </div>
                    <NeedHelpWithPixel />
                  </div>
                  <VerifySetupWrapper
                    subStep="c"
                    subStepRef={cTitleRef}
                    stepMetadata={stepMetadata}
                    updateStepMetadata={updateStepMetadata}
                    isMarkedComplete={isMarkedComplete}
                    domainName={domainName ?? undefined}
                  />
                </>
              )}
              {stepMetadata?.provider === "other" && (
                <>
                  <div
                    ref={bTitleRef}
                    className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10"
                  >
                    <div className="text-slate-900 text-2xl font-semibold leading-loose">
                      {stepIndex}b. Please install the pixel on every page of
                      your site
                    </div>
                    <div className="flex flex-row gap-2 items-center">
                      <div className="h-1 w-1 rounded-full bg-interstellar+0"></div>
                      <div>
                        You’ll need to install this script in the{" "}
                        <code>{"<head>"}</code> section of every page on your
                        site:
                      </div>
                    </div>
                    <WarningPanel>
                      <span className="text-lg font-semibold leading-normal">
                        Do not change the script
                      </span>
                    </WarningPanel>
                    <div className="w-full">
                      <OnboardingTrackingScript
                        displayName="Northbeam Pixel"
                        header={
                          "Please place this pixel on every page of your website. This is " +
                          "Northbeam's primary pixel."
                        }
                        scriptCode={
                          data?.me?.installationScripts.northbeam_pixel
                        }
                        descriptionHTML={
                          <>
                            <div className="flex flex-row gap-2 items-center">
                              <div className="h-1 w-1 rounded-full bg-interstellar+0"></div>
                              If you use Google Tag Manager, we recommend
                              following Northbeam’s{" "}
                              <a
                                href="https://docs.northbeam.io/docs/google-tag-manager-installation"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Google Tag Manager instructions
                              </a>
                            </div>
                          </>
                        }
                      />
                    </div>
                  </div>
                  <div
                    ref={cTitleRef}
                    className="px-10 pt-10 pb-20 flex-col justify-start items-start gap-6 inline-flex border-b border-gray-0 scroll-m-10"
                  >
                    <div className="text-slate-900 text-2xl font-semibold leading-loose">
                      {stepIndex}c. Please implement the 'firePurchaseEvent'
                      Javascript function at conversion points
                    </div>
                    <ul className="list-disc pt-2 pl-6">
                      <li className="pb-2">
                        After embedding the pixel on every single page you will
                        need to implement the <code>firePurchaseEvent</code>{" "}
                        JavaScript function at every conversion point in your
                        store.
                      </li>
                      <li className="pb-2">
                        The <code>firePurchaseEvent</code> function at every
                        conversion point in your store.
                      </li>
                      <li>
                        The <code>firePurchaseEvent</code> data should match
                        what you upload to Northbeam through the orders API.
                      </li>
                    </ul>
                    <NeedHelpWithPixel
                      link="https://docs.northbeam.io/docs/standard-installation#implement-firepurchaseevent-to-track-purchases"
                      text="Need help setting up the 'firePurchaseEvent'?"
                    />
                  </div>
                  <VerifySetupWrapper
                    subStep="d"
                    subStepRef={dTitleRef}
                    stepMetadata={stepMetadata}
                    updateStepMetadata={updateStepMetadata}
                    isMarkedComplete={isMarkedComplete}
                    domainName={domainName ?? undefined}
                  />
                </>
              )}
              {error && (
                <Error content="There was an error loading your settings." />
              )}
            </>
          )}
        </Step>
      </StepContainer>
    </StepWizard>
  );
};

function isPixelVerified(stepMetadata: any): boolean {
  const status = stepMetadata?.pixelVerification;
  return status && status.isConversionTracking && status.isPixelInstalled;
}

const VerifySetupWrapper = ({
  subStep,
  subStepRef,
  stepMetadata,
  updateStepMetadata,
  isMarkedComplete,
  domainName,
}: {
  subStep: "c" | "d";
  subStepRef: (node: any) => void;
  stepMetadata: any;
  updateStepMetadata: (update: any) => void;
  isMarkedComplete?: boolean;
  domainName?: string;
}) => {
  return (
    <VerifySetup
      subStep={subStep}
      subStepRef={subStepRef}
      isSetupConfirmed={stepMetadata?.isSetupConfirmed}
      setSetupConfirmed={(isConfirmed: boolean) =>
        updateStepMetadata({
          ...stepMetadata,
          isSetupConfirmed: isConfirmed,
        })
      }
      pixelVerification={stepMetadata?.pixelVerification}
      setPixelVerification={(status?: PixelVerificationStatus) => {
        const newStepMetadata = status
          ? {
              ...stepMetadata,
              pixelVerification: status,
            }
          : // remove pixelVerification entry if status is undefined
            Object.fromEntries(
              Object.entries(stepMetadata).filter(
                ([key]) => key !== "pixelVerification",
              ),
            );
        updateStepMetadata(newStepMetadata);
      }}
      disabled={isMarkedComplete}
      domainName={domainName}
    />
  );
};

export const USER_QUERY = gql`
  query PixelPlacementInfoQuery($connectionType: ConnectionType) {
    me {
      id
      domainName
      installationScripts
      sanitizedConnections(type: $connectionType) {
        id
      }
    }
  }
`;
